import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  publicGiveaways, publicGiveaway, publicGiveawayEnter, publicGiveawayRecheck,
  getGeo, track, captureFbclidFromUrl,
} from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Gift, Trophy, Clock, Users, ArrowLeft, ArrowRight, Sparkles, CheckCircle2,
  Send, Calendar, MapPin, AlertTriangle, XCircle, RefreshCcw, ExternalLink, ShieldCheck, DollarSign, Info,
} from 'lucide-react';

const SITE_LOGO = 'https://customer-assets.emergentagent.com/job_87cd0f70-4f18-4348-a2fd-e2666a61734d/artifacts/ozrawt0t_IMG_0439.jpeg';

function _fp() {
  try {
    if (typeof window === 'undefined') return '';
    return [
      navigator.userAgent || '',
      navigator.language || '',
      navigator.platform || '',
      String(window.screen?.width || '') + 'x' + String(window.screen?.height || ''),
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    ].join('|');
  } catch { return ''; }
}

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
}

function timeLeft(endIso) {
  if (!endIso) return null;
  const end = new Date(endIso).getTime();
  const now = Date.now();
  const diff = end - now;
  if (diff <= 0) return { label: 'Ended', urgent: false, ended: true };
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (days >= 2) return { label: `${days} days left`, urgent: false, ended: false };
  if (days >= 1) return { label: `${days}d ${hours}h left`, urgent: true, ended: false };
  if (hours >= 1) return { label: `${hours}h ${mins}m left`, urgent: true, ended: false };
  return { label: `${mins}m left — last chance!`, urgent: true, ended: false };
}

/**
 * Live-ticking countdown: returns { d, h, m, s, ended } updated every second.
 */
function useCountdown(endIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!endIso) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [endIso]);
  if (!endIso) return { d: 0, h: 0, m: 0, s: 0, ended: true, totalMs: 0 };
  const end = new Date(endIso).getTime();
  const diff = Math.max(0, end - now);
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
    ended: diff <= 0,
    totalMs: diff,
  };
}

function pad(n) { return String(n).padStart(2, '0'); }

/**
 * Compact countdown (for list cards) — single inline pill: 12d 04h 23m 17s
 */
function CountdownPill({ endIso, comingSoon = false }) {
  const c = useCountdown(endIso);
  if (!endIso) return null;
  if (c.ended) {
    return (
      <Badge className="bg-[rgba(225,29,46,0.12)] text-[hsl(0_72%_60%)] border border-[rgba(225,29,46,0.32)] text-[10px] gap-1">
        <Clock className="h-3 w-3" /> {comingSoon ? 'Starting…' : 'Ended'}
      </Badge>
    );
  }
  const urgent = !comingSoon && c.totalMs < 86400000;
  const cls = comingSoon
    ? 'bg-[rgba(108,176,255,0.10)] text-[hsl(212_80%_70%)] border border-[rgba(108,176,255,0.32)]'
    : urgent
      ? 'bg-[rgba(225,29,46,0.12)] text-[hsl(0_72%_60%)] border border-[rgba(225,29,46,0.32)]'
      : 'bg-[rgba(245,185,66,0.10)] text-[hsl(43_92%_56%)] border border-[rgba(245,185,66,0.28)]';
  return (
    <Badge className={cls + ' text-[10px] gap-1 font-mono tabular-nums'}>
      <Clock className="h-3 w-3" />
      {comingSoon && <span className="font-sans">Starts in</span>}
      {c.d > 0 && <span>{c.d}d</span>}
      <span>{pad(c.h)}h</span>
      <span>{pad(c.m)}m</span>
      <span>{pad(c.s)}s</span>
    </Badge>
  );
}

/**
 * Large 4-block countdown for detail page — Days / Hours / Minutes / Seconds.
 */
function CountdownBlocks({ endIso, comingSoon = false }) {
  const c = useCountdown(endIso);
  if (!endIso) return null;
  const urgent = !comingSoon && !c.ended && c.totalMs < 86400000;
  const items = [
    { label: 'Days', val: c.d },
    { label: 'Hours', val: c.h },
    { label: 'Minutes', val: c.m },
    { label: 'Seconds', val: c.s },
  ];
  const accent = comingSoon
    ? { border: 'border-[rgba(108,176,255,0.32)]', bg: 'from-[rgba(108,176,255,0.10)] to-[rgba(108,176,255,0.02)]', text: 'text-[hsl(212_80%_70%)]' }
    : urgent
      ? { border: 'border-[rgba(225,29,46,0.32)]', bg: 'from-[rgba(225,29,46,0.10)] to-[rgba(225,29,46,0.02)]', text: 'text-[hsl(0_72%_60%)]' }
      : { border: 'border-[rgba(245,185,66,0.28)]', bg: 'from-[rgba(245,185,66,0.10)] to-[rgba(245,185,66,0.02)]', text: 'text-[hsl(43_92%_56%)]' };
  return (
    <div className="space-y-2.5" data-testid="countdown-blocks">
      <div className={'flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] ' + accent.text}>
        <Clock className="h-3 w-3" /> {c.ended ? (comingSoon ? 'Starting now' : 'Giveaway ended') : (comingSoon ? 'Starts in' : 'Ends in')}
      </div>
      <div className="grid grid-cols-4 gap-2 sm:gap-3">
        {items.map((it) => (
          <div key={it.label}
            className={'rounded-lg border bg-gradient-to-b backdrop-blur-sm px-2 py-3 sm:py-4 text-center transition-colors ' + accent.border + ' ' + accent.bg}
          >
            <div className={'font-display tabular-nums leading-none text-3xl sm:text-4xl ' + accent.text}
              data-testid={`cd-${it.label.toLowerCase()}`}>
              {pad(it.val)}
            </div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground mt-1.5 sm:mt-2">
              {it.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Header() {
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-[hsl(0_0%_3%)]/80 border-b border-border">
      <div className="container-x flex items-center justify-between h-14">
        <Link to="/" className="flex items-center gap-2 text-sm font-semibold">
          <img src={SITE_LOGO} alt="AlveeTrade" className="h-7 w-7 rounded-full object-cover" />
          <span>AlveeTrade</span>
        </Link>
        <Link to="/" className="text-xs text-muted-foreground hover:text-white inline-flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to home
        </Link>
      </div>
    </header>
  );
}

function GiveawayCard({ gv, comingSoon = false }) {
  return (
    <Link to={`/giveaways/${gv.slug}`} className="group block" data-testid={`giveaway-card-${gv.slug}`}>
      <Card className={'card-surface overflow-hidden h-full transition-transform duration-200 group-hover:-translate-y-1 ' + (comingSoon ? 'border border-[rgba(108,176,255,0.32)]' : 'gold-hairline')}>
        <div className={'aspect-[16/9] overflow-hidden flex items-center justify-center relative ' + (comingSoon ? 'bg-gradient-to-br from-[hsl(212_80%_70%)]/10 via-[hsl(0_0%_4%)] to-[hsl(0_0%_4%)]' : 'bg-gradient-to-br from-[hsl(43_92%_56%)]/10 via-[hsl(0_0%_4%)] to-[hsl(0_0%_4%)]')}>
          {gv.cover_image_url ? (
            <img src={gv.cover_image_url} alt={gv.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy" />
          ) : (
            <Gift className={'h-16 w-16 ' + (comingSoon ? 'text-[hsl(212_80%_70%)]/50' : 'text-[hsl(43_92%_56%)]/50')} />
          )}
          {comingSoon && (
            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[hsl(212_80%_70%)] text-black text-[10px] font-bold uppercase tracking-[0.18em] shadow-lg inline-flex items-center gap-1.5 z-10">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-black animate-pulse" />
              Coming Soon
            </div>
          )}
          {gv.prize_label && (
            <div className="absolute top-3 right-3 max-w-[55%] px-2.5 py-1 rounded-full bg-[hsl(43_92%_56%)] text-black text-[10px] font-bold uppercase tracking-[0.18em] shadow-lg truncate">
              {gv.prize_label}
            </div>
          )}
        </div>
        <CardContent className="p-4 space-y-2.5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>{comingSoon ? `Starts ${fmtDate(gv.starts_at)}` : `Ends ${fmtDate(gv.ends_at)}`}</span>
            {!comingSoon && gv.entries_count != null && (
              <>
                <span className="text-muted-foreground/40">·</span>
                <Users className="h-3 w-3" />
                <span>{gv.entries_count.toLocaleString()} entries</span>
              </>
            )}
          </div>
          <h3 className="font-display uppercase text-lg leading-tight line-clamp-2">{gv.title}</h3>
          {gv.description && (
            <p className="text-xs text-muted-foreground line-clamp-3">{gv.description}</p>
          )}
          <div className="flex items-center justify-between pt-1.5 border-t border-white/5 gap-2 flex-wrap">
            <CountdownPill endIso={comingSoon ? gv.starts_at : gv.ends_at} comingSoon={comingSoon} />
            <span className={'text-xs inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform ' + (comingSoon ? 'text-[hsl(212_80%_70%)]' : 'text-[hsl(43_92%_56%)]')}>
              {comingSoon ? 'View details' : 'Enter now'} <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function WinnerCard({ gv }) {
  const w = gv.winner;
  return (
    <Card className="card-surface overflow-hidden gold-hairline" data-testid={`winner-card-${gv.slug}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="h-12 w-12 rounded-full bg-[rgba(245,185,66,0.12)] border border-[rgba(245,185,66,0.4)] flex items-center justify-center flex-shrink-0">
          <Trophy className="h-5 w-5 text-[hsl(43_92%_56%)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(43_92%_56%)] mb-0.5">Winner · {fmtDate(gv.winner_picked_at)}</div>
          <div className="font-semibold truncate">{gv.title}</div>
          {w ? (
            <div className="text-xs text-muted-foreground truncate">
              <span className="text-[hsl(0_0%_92%)]">{w.name}</span>
              <span className="mx-1">·</span>
              <span>{w.email_masked}</span>
              {w.country_code && <span className="ml-1">· {w.country_code}</span>}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">No eligible entries — winner not awarded</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ListView() {
  const [data, setData] = useState({ coming_soon: [], active: [], past_winners: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Track page visit → fires PageView via CAPI (server) + browser pixel
    (async () => {
      try {
        captureFbclidFromUrl();
        const g = await getGeo();
        const params = new URLSearchParams(window.location.search);
        track({
          type: 'visit',
          country_code: g?.country_code || null,
          country_name: g?.country_name || null,
          source: 'giveaways_list',
          utm_source: params.get('utm_source'),
          utm_campaign: params.get('utm_campaign'),
          utm_medium: params.get('utm_medium'),
          referrer: typeof document !== 'undefined' ? document.referrer : null,
        });
      } catch {}
    })();
    publicGiveaways()
      .then((d) => setData({ coming_soon: d?.coming_soon || [], active: d?.active || [], past_winners: d?.past_winners || [] }))
      .catch(() => setData({ coming_soon: [], active: [], past_winners: [] }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="bg-[hsl(0_0%_4%)] min-h-screen text-white">
      <Header />
      {/* Hero */}
      <section className="section-pad relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{ background: 'radial-gradient(60% 50% at 50% 0%, rgba(245,185,66,0.10), transparent 70%)' }}
        />
        <div className="container-x relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.28em] text-[hsl(43_92%_56%)] mb-3">
              <Sparkles className="h-3 w-3" /> Live Giveaways
            </div>
            <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Win Real Prizes.<br />Real Members.
            </h1>
            <p className="text-muted-foreground mt-4 text-base md:text-lg leading-relaxed">
              Enter active giveaways below for cash, trading accounts, signal subscriptions and more.
              One entry per email + IP — winner is auto-picked when the timer runs out.
            </p>
          </div>
        </div>
      </section>

      {/* Coming Soon list */}
      {data.coming_soon.length > 0 && (
        <section className="container-x pb-10" data-testid="giveaways-coming-soon-section">
          <div className="flex items-end justify-between mb-5">
            <h2 className="font-display uppercase text-xl sm:text-2xl inline-flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-[hsl(212_80%_70%)] animate-pulse" />
              Coming Soon
            </h2>
            <span className="text-[11px] uppercase tracking-[0.18em] text-[hsl(212_80%_70%)]">
              {data.coming_soon.length} upcoming
            </span>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {data.coming_soon.map((gv) => <GiveawayCard key={gv.id} gv={gv} comingSoon />)}
          </div>
        </section>
      )}

      {/* Active list */}
      <section className="container-x pb-12">
        <div className="flex items-end justify-between mb-5">
          <h2 className="font-display uppercase text-xl sm:text-2xl">Active Giveaways</h2>
          <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            {loading ? 'Loading…' : `${data.active.length} live`}
          </span>
        </div>
        {loading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Card key={i} className="card-surface overflow-hidden">
                <div className="aspect-[16/9] bg-white/[0.04] animate-pulse" />
                <CardContent className="p-4 space-y-3">
                  <div className="h-3 w-20 bg-white/[0.06] rounded animate-pulse" />
                  <div className="h-5 w-3/4 bg-white/[0.06] rounded animate-pulse" />
                  <div className="h-3 w-full bg-white/[0.04] rounded animate-pulse" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : data.active.length === 0 ? (
          <Card className="card-surface">
            <CardContent className="p-10 text-center text-sm text-muted-foreground">
              <Gift className="h-10 w-10 opacity-30 mx-auto mb-3" />
              {data.coming_soon.length > 0
                ? "No active giveaways right now — check the Coming Soon section above."
                : "No active giveaways right now. Check back soon — new prizes drop every week."}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {data.active.map((gv) => <GiveawayCard key={gv.id} gv={gv} />)}
          </div>
        )}
      </section>

      {/* Past winners */}
      {data.past_winners.length > 0 && (
        <section className="container-x pb-16">
          <div className="flex items-end justify-between mb-5">
            <h2 className="font-display uppercase text-xl sm:text-2xl">Past Winners</h2>
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              {data.past_winners.length} announced
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {data.past_winners.map((gv) => <WinnerCard key={gv.id} gv={gv} />)}
          </div>
        </section>
      )}
    </main>
  );
}

function EligibilityResult({ eligibility, brokerLinks, onRecheck, rechecking, gv }) {
  const status = eligibility?.status || 'exempt';
  const required = Number(eligibility?.required_usd ?? 0);
  const verifiedDep = Number(eligibility?.verified_deposit_usd ?? 0);
  const verifiedWd = Number(eligibility?.verified_withdraw_usd ?? 0);
  const net = Number(eligibility?.net_deposit_usd ?? 0);
  const deficit = Number(eligibility?.deficit_usd ?? 0);
  const progress = required > 0 ? Math.min(100, Math.round((Math.max(0, net) / required) * 100)) : 100;
  // Defensive: ensure message is always a string (never crash on object/array)
  const msg = (() => {
    const m = eligibility?.message;
    if (typeof m === 'string') return m;
    if (m == null) return '';
    try { return JSON.stringify(m); } catch { return String(m); }
  })();

  // ---------- ELIGIBLE / EXEMPT ----------
  if (status === 'eligible' || status === 'exempt') {
    return (
      <div className="text-center py-2" data-testid="result-eligible">
        <div className="mx-auto h-14 w-14 rounded-full bg-[rgba(34,199,132,0.10)] border border-[rgba(34,199,132,0.4)] flex items-center justify-center mb-3">
          <CheckCircle2 className="h-8 w-8 text-[hsl(152_78%_55%)]" />
        </div>
        <div className="text-base font-semibold mb-1">You're in! 🎉</div>
        <div className="text-xs text-muted-foreground leading-relaxed mb-3 px-2">
          {status === 'exempt'
            ? "Winner will be picked automatically when the countdown hits zero. We'll announce on this page and the AlveeTrade Telegram."
            : (msg || "Eligibility verified. Stay tuned for the winner announcement.")}
        </div>
        {required > 0 && (
          <div className="rounded-md border border-[rgba(34,199,132,0.32)] bg-[rgba(34,199,132,0.06)] p-3 text-left text-xs space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Required deposit</span>
              <span className="font-mono">${required.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Your deposits</span>
              <span className="font-mono text-[hsl(152_78%_55%)]">${verifiedDep.toFixed(2)}</span>
            </div>
            {verifiedWd > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Withdrawals</span>
                <span className="font-mono text-[hsl(0_72%_60%)]">−${verifiedWd.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-white/5 pt-1 mt-1">
              <span className="text-muted-foreground">Net (verified)</span>
              <span className="font-mono text-[hsl(43_92%_56%)]">${net.toFixed(2)}</span>
            </div>
          </div>
        )}
        {required > 0 && (
          <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
            ⚠ Withdrawing during the giveaway period will automatically cancel your eligibility.
          </p>
        )}
      </div>
    );
  }

  // ---------- PENDING (account verified, deposit insufficient) ----------
  if (status === 'pending') {
    const poRedirect = brokerLinks?.pocket_option_redirect;
    const qxRedirect = brokerLinks?.quotex_redirect;
    return (
      <div className="py-1 space-y-3" data-testid="result-pending">
        <div className="text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-[rgba(245,185,66,0.10)] border border-[rgba(245,185,66,0.4)] flex items-center justify-center mb-3">
            <AlertTriangle className="h-8 w-8 text-[hsl(43_92%_56%)]" />
          </div>
          <div className="text-base font-semibold mb-1">Almost there — deposit more</div>
          <div className="text-xs text-muted-foreground leading-relaxed px-2">
            {msg || `Net deposit ${net} below the ${required} requirement.`}
          </div>
        </div>
        {/* Progress bar */}
        <div>
          <div className="flex items-end justify-between text-[10px] mb-1">
            <span className="text-muted-foreground uppercase tracking-[0.18em]">Progress</span>
            <span className="font-mono text-[hsl(43_92%_56%)]">${net.toFixed(2)} / ${required.toFixed(2)}</span>
          </div>
          <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[hsl(43_92%_56%)] to-[hsl(43_92%_70%)] transition-all duration-500"
              style={{ width: `${progress}%` }} />
          </div>
        </div>
        <div className="rounded-md border border-[rgba(245,185,66,0.28)] bg-[rgba(245,185,66,0.04)] p-3 text-xs space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Required</span>
            <span className="font-mono">${required.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Deposited so far</span>
            <span className="font-mono">${verifiedDep.toFixed(2)}</span>
          </div>
          {verifiedWd > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Withdrawn</span>
              <span className="font-mono text-[hsl(0_72%_60%)]">−${verifiedWd.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between font-semibold border-t border-white/5 pt-1 mt-1">
            <span className="text-[hsl(43_92%_56%)]">Still need to deposit</span>
            <span className="font-mono text-[hsl(43_92%_56%)]">${deficit.toFixed(2)}</span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {poRedirect && (
            <a href={poRedirect} target="_blank" rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 h-10 rounded-md bg-[rgba(245,185,66,0.14)] border border-[rgba(245,185,66,0.42)] text-[hsl(43_92%_56%)] text-xs font-semibold hover:bg-[rgba(245,185,66,0.22)] transition-colors"
              data-testid="result-pending-po-deposit">
              <DollarSign className="h-3.5 w-3.5" /> Deposit on Pocket Option
            </a>
          )}
          {qxRedirect && (
            <a href={qxRedirect} target="_blank" rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 h-10 rounded-md bg-[rgba(108,176,255,0.14)] border border-[rgba(108,176,255,0.42)] text-[hsl(212_80%_70%)] text-xs font-semibold hover:bg-[rgba(108,176,255,0.22)] transition-colors"
              data-testid="result-pending-qx-deposit">
              <DollarSign className="h-3.5 w-3.5" /> Deposit on Quotex
            </a>
          )}
          <Button type="button" variant="outline" onClick={onRecheck} disabled={rechecking}
            className="w-full h-10 border-white/10 bg-white/[0.02] text-xs gap-2"
            data-testid="result-recheck-btn">
            <RefreshCcw className={'h-3.5 w-3.5 ' + (rechecking ? 'animate-spin' : '')} />
            {rechecking ? 'Re-checking…' : 'Re-check status'}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground leading-relaxed text-center">
          ⏱ After depositing, wait 1–2 minutes for the broker postback, then click <strong>Re-check status</strong>.
        </p>
      </div>
    );
  }

  // ---------- NO ACCOUNT ----------
  const poRedirect = brokerLinks?.pocket_option_redirect;
  const qxRedirect = brokerLinks?.quotex_redirect;
  return (
    <div className="py-1 space-y-3" data-testid="result-no-account">
      <div className="text-center">
        <div className="mx-auto h-14 w-14 rounded-full bg-[rgba(225,29,46,0.10)] border border-[rgba(225,29,46,0.4)] flex items-center justify-center mb-3">
          <XCircle className="h-8 w-8 text-[hsl(0_72%_60%)]" />
        </div>
        <div className="text-base font-semibold mb-1">Trader ID not detected</div>
        <div className="text-xs text-muted-foreground leading-relaxed px-2">
          {msg || "We couldn't find that Trader ID in our tracking."}
        </div>
      </div>
      <div className="rounded-md border border-[rgba(245,185,66,0.28)] bg-[rgba(245,185,66,0.06)] p-3.5 space-y-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[hsl(43_92%_56%)] font-semibold">
          <Info className="h-3.5 w-3.5" /> How to qualify — 3 steps
        </div>
        <ol className="space-y-2 text-xs text-muted-foreground leading-relaxed list-decimal pl-4">
          <li><strong className="text-white">Register</strong> using ONE of OUR affiliate links below (this is how we attach a tracking ID).</li>
          <li><strong className="text-white">Deposit at least ${required.toFixed(2)}</strong> on that broker account before the giveaway ends.</li>
          <li>Come back here and <strong className="text-white">enter the giveaway</strong> using the same Trader ID — we'll auto-verify your deposit.</li>
        </ol>
      </div>
      <div className="flex flex-col gap-2">
        {poRedirect && (
          <a href={poRedirect} target="_blank" rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 h-11 rounded-md bg-[hsl(43_92%_56%)] text-black font-bold text-xs uppercase tracking-[0.16em] hover:bg-[hsl(43_92%_50%)] transition-colors"
            data-testid="result-no-account-po-register">
            <ShieldCheck className="h-4 w-4" /> Register · Pocket Option
            <ExternalLink className="h-3 w-3 opacity-70" />
          </a>
        )}
        {qxRedirect && (
          <a href={qxRedirect} target="_blank" rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 h-11 rounded-md bg-[hsl(212_80%_70%)] text-black font-bold text-xs uppercase tracking-[0.16em] hover:bg-[hsl(212_80%_64%)] transition-colors"
            data-testid="result-no-account-qx-register">
            <ShieldCheck className="h-4 w-4" /> Register · Quotex
            <ExternalLink className="h-3 w-3 opacity-70" />
          </a>
        )}
        <Button type="button" variant="outline" onClick={onRecheck} disabled={rechecking}
          className="w-full h-10 border-white/10 bg-white/[0.02] text-xs gap-2"
          data-testid="result-recheck-btn">
          <RefreshCcw className={'h-3.5 w-3.5 ' + (rechecking ? 'animate-spin' : '')} />
          {rechecking ? 'Re-checking…' : 'Already registered? Re-check'}
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground leading-relaxed text-center">
        🔒 We can only verify accounts created using our affiliate links. Direct sign-ups won't be tracked.
      </p>
    </div>
  );
}

function DetailView() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [gv, setGv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [eligibility, setEligibility] = useState(null);  // { status, message, ... } from server
  const [brokerLinks, setBrokerLinks] = useState({ pocket_option_redirect: null, quotex_redirect: null });
  const [rechecking, setRechecking] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', telegram_username: '', pocket_option_id: '', quotex_id: '', whatsapp: '', facebook: '', custom: {},
  });

  const load = useCallback(async () => {
    setLoading(true);
    try { setGv(await publicGiveaway(slug)); }
    catch { setGv(null); }
    finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // Fire visit event on giveaway detail page load → CAPI PageView
  useEffect(() => {
    (async () => {
      try {
        const g = await getGeo();
        const params = new URLSearchParams(window.location.search);
        track({
          type: 'visit',
          country_code: g?.country_code || null,
          country_name: g?.country_name || null,
          source: `giveaway_detail:${slug}`,
          utm_source: params.get('utm_source'),
          utm_campaign: params.get('utm_campaign'),
          utm_medium: params.get('utm_medium'),
          referrer: typeof document !== 'undefined' ? document.referrer : null,
        });
      } catch {}
    })();
  }, [slug]);

  const tl = useMemo(() => gv && timeLeft(gv.ends_at), [gv]);
  // Force re-render once a second so the "ended" gate flips live without page reload.
  const [, _tick] = useState(0);
  useEffect(() => {
    if (!gv?.ends_at) return undefined;
    const id = setInterval(() => _tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [gv?.ends_at]);

  function up(k, v) { setForm((s) => ({ ...s, [k]: v })); }
  function upCustom(k, v) {
    setForm((s) => ({ ...s, custom: { ...s.custom, [k]: v } }));
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await publicGiveawayEnter(slug, { ...form, fingerprint: _fp() });
      setEligibility(res?.eligibility || null);
      setBrokerLinks(res?.broker_links || {});
      setDone(true);
      const st = res?.eligibility?.status;
      if (st === 'eligible' || st === 'exempt') {
        toast.success('You\'re in! Winner will be announced when timer ends.');
      } else if (st === 'pending') {
        toast.warning('Account verified — but more deposit needed to qualify.');
      } else {
        toast.success('Entry submitted.');
      }
    } catch (err) {
      // Backend rejects no_account with structured 422 payload
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 422 && detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code === 'no_account') {
        setEligibility(detail.eligibility || { status: 'no_account', message: detail.message });
        setBrokerLinks(detail.broker_links || {});
        setDone(true);
        toast.error('Trader ID not linked. Register via our affiliate link to participate.');
      } else {
        // Normalize FastAPI validation errors (array) and object details into a string
        let msg = 'Entry failed';
        if (typeof detail === 'string') msg = detail;
        else if (Array.isArray(detail) && detail.length) msg = detail[0]?.msg || msg;
        else if (detail && typeof detail === 'object') msg = detail.message || detail.msg || msg;
        toast.error(msg);
      }
    } finally { setSubmitting(false); }
  }

  async function recheck() {
    if (rechecking) return;
    // Need at least a trader ID or email to identify the entry
    if (!form.pocket_option_id && !form.quotex_id && !form.email) {
      toast.error('Enter your Trader ID or email to re-check.');
      return;
    }
    setRechecking(true);
    try {
      const res = await publicGiveawayRecheck(slug, {
        email: form.email || '',
        pocket_option_id: form.pocket_option_id || '',
        quotex_id: form.quotex_id || '',
      });
      setEligibility(res?.eligibility || null);
      const st = res?.eligibility?.status;
      if (st === 'eligible') toast.success('Eligibility confirmed!');
      else if (st === 'pending') toast.warning('Still pending — deposit more to qualify.');
      else if (st === 'no_account') toast.error('Account still not detected.');
      else toast.info('Status updated.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = 'Recheck failed';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail.length) msg = detail[0]?.msg || msg;
      else if (detail && typeof detail === 'object') msg = detail.message || detail.msg || msg;
      toast.error(msg);
    } finally { setRechecking(false); }
  }

  if (loading) {
    return (
      <main className="bg-[hsl(0_0%_4%)] min-h-screen text-white">
        <Header />
        <div className="container-x py-16 text-sm text-muted-foreground">Loading…</div>
      </main>
    );
  }

  if (!gv) {
    return (
      <main className="bg-[hsl(0_0%_4%)] min-h-screen text-white">
        <Header />
        <div className="container-x py-16 text-center">
          <h1 className="font-display uppercase text-2xl mb-3">Giveaway not found</h1>
          <Button onClick={() => navigate('/giveaways')} variant="outline" className="border-white/10 bg-white/[0.02]">
            <ArrowLeft className="h-4 w-4 mr-2" /> All giveaways
          </Button>
        </div>
      </main>
    );
  }

  const isComingSoon = gv.status === 'coming_soon';
  const ended = !isComingSoon && (gv.status !== 'active' || (tl && tl.ended) || (gv.ends_at && new Date(gv.ends_at).getTime() <= Date.now()));
  const w = gv.winner;

  return (
    <main className="bg-[hsl(0_0%_4%)] min-h-screen text-white">
      <Header />
      <div className="container-x py-8">
        <Link to="/giveaways" className="text-xs text-muted-foreground hover:text-white inline-flex items-center gap-1 mb-5">
          <ArrowLeft className="h-3.5 w-3.5" /> All giveaways
        </Link>

        <div className="grid gap-8 lg:grid-cols-[1fr,400px]">
          {/* Left — info */}
          <div className="space-y-5">
            {gv.cover_image_url && (
              <div className="rounded-2xl overflow-hidden gold-hairline aspect-[16/9] bg-black">
                <img src={gv.cover_image_url} alt={gv.title} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="space-y-3">
              {gv.prize_label && (
                <Badge className="bg-[hsl(43_92%_56%)] text-black text-[10px] uppercase tracking-[0.2em] font-bold">
                  {gv.prize_label}
                </Badge>
              )}
              <h1 className="font-display uppercase text-3xl sm:text-4xl leading-[0.95]" data-testid="giveaway-title">
                {gv.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {isComingSoon ? `Starts ${fmtDate(gv.starts_at)}` : `Ends ${fmtDate(gv.ends_at)}`}</span>
                {!isComingSoon && gv.entries_count != null && (
                  <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {gv.entries_count.toLocaleString()} entries</span>
                )}
                <CountdownPill endIso={isComingSoon ? gv.starts_at : gv.ends_at} comingSoon={isComingSoon} />
                {Number(gv.min_deposit_usd || 0) > 0 && (
                  <span className="inline-flex items-center gap-1.5 text-[hsl(43_92%_56%)]" data-testid="min-deposit-pill">
                    <DollarSign className="h-3 w-3" /> ${Number(gv.min_deposit_usd).toLocaleString()} min deposit
                  </span>
                )}
              </div>
              {gv.description && (
                <div className="text-sm text-[hsl(0_0%_88%)] leading-relaxed whitespace-pre-line">
                  {gv.description}
                </div>
              )}
              {gv.status === 'active' && (
                <div className="pt-2">
                  <CountdownBlocks endIso={gv.ends_at} />
                </div>
              )}
              {isComingSoon && gv.starts_at && (
                <div className="pt-2">
                  <CountdownBlocks endIso={gv.starts_at} comingSoon />
                </div>
              )}
            </div>

            {/* Winner announcement (if picked) */}
            {gv.status === 'winner_picked' && (
              <Card className="card-surface gold-hairline">
                <CardContent className="p-5 flex items-start gap-3">
                  <Trophy className="h-6 w-6 text-[hsl(43_92%_56%)] mt-0.5" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(43_92%_56%)] mb-1">Winner picked</div>
                    {w ? (
                      <div className="text-sm">
                        Congratulations <strong>{w.name}</strong> ({w.email_masked})
                        {w.country_code && <span> from <strong>{w.country_code}</strong></span>}!
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">No eligible entries — no winner.</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right — entry form */}
          <aside>
            <Card className="card-surface gold-hairline lg:sticky lg:top-20">
              <CardContent className="p-5 space-y-4">
                {isComingSoon ? (
                  <div className="text-center py-4">
                    <Clock className="h-8 w-8 text-[hsl(212_80%_70%)] mx-auto mb-2" />
                    <div className="text-sm font-semibold mb-1">Entries open soon</div>
                    <div className="text-xs text-muted-foreground mb-3">
                      This giveaway hasn't started yet. Come back when the countdown ends.
                    </div>
                    {gv.starts_at && <CountdownPill endIso={gv.starts_at} comingSoon />}
                    <div className="mt-4">
                      <Button onClick={() => navigate('/giveaways')} variant="outline"
                        className="border-white/10 bg-white/[0.02] gap-1 text-xs">
                        <ArrowLeft className="h-3.5 w-3.5" /> All giveaways
                      </Button>
                    </div>
                  </div>
                ) : ended ? (
                  <div className="text-center py-4">
                    <AlertTriangle className="h-8 w-8 text-[hsl(43_92%_56%)] mx-auto mb-2" />
                    <div className="text-sm font-semibold mb-1">This giveaway has ended</div>
                    <div className="text-xs text-muted-foreground">Check the active giveaways list for live ones.</div>
                    <Button onClick={() => navigate('/giveaways')} variant="outline"
                      className="mt-4 border-white/10 bg-white/[0.02] gap-1 text-xs">
                      <ArrowLeft className="h-3.5 w-3.5" /> Active giveaways
                    </Button>
                  </div>
                ) : done ? (
                  <EligibilityResult
                    eligibility={eligibility}
                    brokerLinks={brokerLinks}
                    onRecheck={recheck}
                    rechecking={rechecking}
                    gv={gv}
                  />
                ) : (
                  <form onSubmit={submit} className="space-y-3" data-testid="giveaway-entry-form">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(43_92%_56%)] flex items-center gap-1">
                      <Gift className="h-3 w-3" /> Enter the giveaway
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Full name *</Label>
                      <Input required value={form.name} onChange={(e) => up('name', e.target.value)} maxLength={80}
                        className="mt-1 bg-white/[0.02] border-white/10 text-sm" data-testid="giveaway-name" />
                    </div>
                    {gv.require_email !== false ? (
                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Email *</Label>
                        <Input required type="email" value={form.email} onChange={(e) => up('email', e.target.value)} maxLength={200}
                          className="mt-1 bg-white/[0.02] border-white/10 text-sm" data-testid="giveaway-email" />
                      </div>
                    ) : (
                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Email (optional)</Label>
                        <Input type="email" value={form.email} onChange={(e) => up('email', e.target.value)} maxLength={200}
                          placeholder="So we can notify you if you win"
                          className="mt-1 bg-white/[0.02] border-white/10 text-sm" data-testid="giveaway-email" />
                      </div>
                    )}
                    {!gv.require_telegram && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                          Telegram username (optional)
                        </Label>
                        <Input value={form.telegram_username} placeholder="@your_handle"
                          onChange={(e) => up('telegram_username', e.target.value.replace(/^@/, ''))}
                          maxLength={50}
                          className="mt-1 bg-white/[0.02] border-white/10 text-sm" data-testid="giveaway-tg" />
                      </div>
                    )}
                    {gv.require_telegram && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Telegram username *</Label>
                        <Input required value={form.telegram_username} placeholder="@your_handle"
                          onChange={(e) => up('telegram_username', e.target.value.replace(/^@/, ''))}
                          maxLength={50}
                          className="mt-1 bg-white/[0.02] border-white/10 text-sm" data-testid="giveaway-tg" />
                      </div>
                    )}
                    {gv.require_pocket_option_id && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1.5">
                          <span className="inline-flex items-center justify-center h-4 w-4 rounded-sm bg-[rgba(245,185,66,0.14)] border border-[rgba(245,185,66,0.4)] text-[8px] font-bold text-[hsl(43_92%_56%)]">PO</span>
                          Pocket Option Trader ID *
                        </Label>
                        <Input required value={form.pocket_option_id} placeholder="e.g. 71306942"
                          onChange={(e) => up('pocket_option_id', e.target.value)} maxLength={40}
                          className="mt-1 bg-white/[0.02] border-white/10 text-sm font-mono" data-testid="giveaway-po-id" />
                      </div>
                    )}
                    {gv.require_quotex_id && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1.5">
                          <span className="inline-flex items-center justify-center h-4 w-4 rounded-sm bg-[rgba(108,176,255,0.14)] border border-[rgba(108,176,255,0.4)] text-[8px] font-bold text-[hsl(212_80%_70%)]">QX</span>
                          Quotex Trader ID *
                        </Label>
                        <Input required value={form.quotex_id} placeholder="e.g. 12345678"
                          onChange={(e) => up('quotex_id', e.target.value)} maxLength={40}
                          className="mt-1 bg-white/[0.02] border-white/10 text-sm font-mono" data-testid="giveaway-quotex-id" />
                      </div>
                    )}
                    {gv.require_whatsapp && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">WhatsApp number *</Label>
                        <Input required type="tel" value={form.whatsapp} placeholder="+8801XXXXXXXXX"
                          onChange={(e) => up('whatsapp', e.target.value)} maxLength={40}
                          className="mt-1 bg-white/[0.02] border-white/10 text-sm" data-testid="giveaway-whatsapp" />
                      </div>
                    )}
                    {gv.require_facebook && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Facebook profile *</Label>
                        <Input required value={form.facebook} placeholder="facebook.com/yourname or @yourname"
                          onChange={(e) => up('facebook', e.target.value)} maxLength={200}
                          className="mt-1 bg-white/[0.02] border-white/10 text-sm" data-testid="giveaway-facebook" />
                      </div>
                    )}
                    {(gv.custom_fields || []).map((cf) => (
                      <div key={cf.key}>
                        <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                          {cf.label} {cf.required && '*'}
                        </Label>
                        <Input
                          required={!!cf.required}
                          type={['email', 'url', 'tel'].includes(cf.type) ? cf.type : 'text'}
                          value={form.custom[cf.key] || ''}
                          onChange={(e) => upCustom(cf.key, e.target.value)} maxLength={300}
                          className="mt-1 bg-white/[0.02] border-white/10 text-sm" data-testid={`giveaway-custom-${cf.key}`}
                        />
                      </div>
                    ))}
                    <Button type="submit" disabled={submitting}
                      className="w-full cta-sheen border-0 text-white font-semibold h-11 gap-2 mt-2"
                      data-testid="giveaway-submit-btn">
                      <Send className="h-4 w-4" /> {submitting ? 'Entering…' : 'Enter giveaway'}
                    </Button>
                    <div className="text-[10px] text-muted-foreground text-center pt-1">
                      One entry per email + IP. Winner picked automatically when timer ends.
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </main>
  );
}

export default function GiveawaysPage({ detail = false }) {
  return detail ? <DetailView /> : <ListView />;
}
